
import React, { useEffect, useState } from 'react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import {
  Box,
  Button,
  Heading,
  VStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Icon,
  HStack,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiCheckCircle, FiX } from 'react-icons/fi';
import Select from 'react-select';
import { getScoreTypes, uploadStoreMaster } from 'services/masterSheetsService';

const BRAND = '#d52b1f';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: 'white',
    borderColor: state.isFocused ? BRAND : '#CBD5E0',
    borderRadius: '8px',
    minHeight: '40px',
    boxShadow: state.isFocused ? `0 0 0 1px ${BRAND}` : 'none',
    '&:hover': { borderColor: '#A0AEC0' },
  }),
  singleValue: (base) => ({ ...base, color: '#2D3748' }),
  placeholder: (base) => ({ ...base, color: '#A0AEC0' }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? BRAND : state.isFocused ? '#EBF8FF' : 'white',
    color: state.isSelected ? 'white' : '#2D3748',
  }),
};

const getCurrentMonthYear = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export default function MasterSheets() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const toast = useToast({
    position: 'top-right',
    isClosable: true,
  });

  const [monthYear, setMonthYear] = useState(getCurrentMonthYear());
  const [year, month] = monthYear.split('-').map(Number);

  const [scoreTypes, setScoreTypes] = useState([]);
  const [scoreTypesLoading, setScoreTypesLoading] = useState(true);
  const [selectedScoreType, setSelectedScoreType] = useState(null);

  const scoreTypeOptions = scoreTypes.map((scoreType) => ({
    value: scoreType.scoretypeId,
    label: scoreType.scoretypeCode,
  }));

  useEffect(() => {
    let isMounted = true;

    const loadScoreTypes = async () => {
      try {
        const response = await getScoreTypes();
        if (isMounted) {
          const activeScoreTypes = (response.data || []).filter((item) => item.isActive);
          setScoreTypes(activeScoreTypes);

          const redScoreType = activeScoreTypes.find((item) => item.scoretypeCode === 'RED');
          if (redScoreType) {
            setSelectedScoreType({
              value: redScoreType.scoretypeId,
              label: redScoreType.scoretypeCode,
            });
          }
        }
      } catch (error) {
        if (isMounted) {
          toast({
            title: 'Error',
            description: 'Failed to load score types',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } finally {
        if (isMounted) setScoreTypesLoading(false);
      }
    };

    loadScoreTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  // File validation
  const validateFile = (file) => {
    const allowedExtensions = ['.xlsx', '.xls'];
    const maxSize = 10 * 1024 * 1024;

    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      return 'Please select a valid Excel file (.xlsx or .xls)';
    }

    if (file.size === 0) {
      return 'Selected file is empty';
    }

    if (file.size > maxSize) {
      return 'File size should not exceed 10MB';
    }

    return null;
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const validationError = validateFile(file);

      if (validationError) {
        setErrorMessage(validationError);
        setSelectedFile(null);
        setUploadStatus('error');
        return;
      }

      setSelectedFile(file);
      setErrorMessage('');
      setUploadStatus(null);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setMonthYear(getCurrentMonthYear());
    const redScoreType = scoreTypes.find((item) => item.scoretypeCode === 'RED');
    setSelectedScoreType(
      redScoreType ? { value: redScoreType.scoretypeId, label: redScoreType.scoretypeCode } : null
    );
    const fileInput = document.getElementById('file-input');
    if (fileInput) fileInput.value = '';
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: 'No file selected',
        description: 'Please select an Excel file to upload',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!selectedScoreType) {
      toast({
        title: 'No score type selected',
        description: 'Please select a score type',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    setUploadStatus(null);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await uploadStoreMaster(
        selectedFile,
        month,
        year,
        selectedScoreType.value
      );

      const message = response.data || 'Upload successful';
      setUploadStatus('success');
      setSuccessMessage(message);
      toast({
        title: 'Upload Successful',
        description: message,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      resetForm();
    } catch (error) {
      setUploadStatus('error');

      if (error.response) {
        const apiMessage =
          typeof error.response.data === 'string' && error.response.data
            ? error.response.data
            : 'Upload failed';
        setErrorMessage(apiMessage);
        toast({
          title: 'Upload Failed',
          description: apiMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        const networkMessage = 'Network error: could not reach the server. Please check your connection and try again.';
        setErrorMessage(networkMessage);
        toast({
          title: 'Network Error',
          description: networkMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setUploadStatus(null);
    setErrorMessage('');
    const fileInput = document.getElementById('file-input');
    if (fileInput) fileInput.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Box py={8}>
      <Box
        pl={0}
        pr={8}
        mb={6}
        mt={10}
        display="flex"
        flexDirection={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        gap={4}
      >
        <Box>
          <Heading size="lg" mb={2}>
            Masters Sheet Upload
          </Heading>
          <Text color="gray.600">
            Upload monthly  Master allocation sheet to update store master data
          </Text>
        </Box>

        <HStack spacing={4} align="flex-end" flexWrap="wrap">
          <Box minW="180px">
            <FormLabel fontSize="md" color="gray.800" fontWeight="bold" mb={1}>
              Score Type
            </FormLabel>
            <Select
              placeholder={scoreTypesLoading ? 'Loading...' : 'Select score type'}
              options={scoreTypeOptions}
              value={selectedScoreType}
              onChange={(option) => setSelectedScoreType(option)}
              isDisabled={scoreTypesLoading || isLoading}
              isLoading={scoreTypesLoading}
              isClearable={false}
              styles={selectStyles}
            />
          </Box>

          <Box minW="210px">
            <FormLabel fontSize="md" color="gray.800" fontWeight="bold" mb={1}>
              Month &amp; Year
            </FormLabel>
            <Box
              as="input"
              type="month"
              value={monthYear}
              onChange={(e) => e.target.value && setMonthYear(e.target.value)}
              disabled={isLoading}
              sx={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'gray.300',
                bg: 'white',
                fontSize: '16px',
                cursor: 'pointer',
                '&:focus': {
                  outline: 'none',
                  borderColor: BRAND,
                  boxShadow: `0 0 0 3px rgba(213,43,31,0.3)`,
                },
              }}
            />
          </Box>
        </HStack>
      </Box>

      <Divider mb={6} />
      <Box display="flex" justifyContent="center" px={8}>
        <Card width="100%" maxW="600px">
          <CardBody>
            <VStack spacing={6} align="stretch">
              <FormControl isInvalid={uploadStatus === 'error' && !!errorMessage}>
                <FormLabel>Select Excel File</FormLabel>
                <Input
                  id="file-input"
                  type="file"
                  accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  onChange={handleFileChange}
                  disabled={isLoading}
                  display="none"
                />
                <Button
                  as="label"
                  htmlFor="file-input"
                  leftIcon={<Icon as={FiFile} />}
                  colorScheme="gray"
                  variant="outline"
                  width="100%"
                  cursor="pointer"
                  isDisabled={isLoading}
                >
                  Choose Excel File
                </Button>
                <FormErrorMessage>{errorMessage}</FormErrorMessage>
              </FormControl>
              {selectedFile && (
                <Box
                  p={4}
                  borderWidth={1}
                  borderRadius="md"
                  borderColor="blue.200"
                  bg="blue.50"
                >
                  <HStack justify="space-between">
                    <HStack spacing={3}>
                      <Icon as={FiFile} color="blue.500" boxSize={5} />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium" fontSize="sm">
                          {selectedFile.name}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {formatFileSize(selectedFile.size)}
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      leftIcon={<Icon as={FiX} />}
                      onClick={handleClear}
                      isDisabled={isLoading}
                    >
                      Remove
                    </Button>
                  </HStack>
                </Box>
              )}
              {isLoading && (
                <Box>
                  <Text mb={2} fontSize="sm" color="gray.600">
                    Uploading and processing...
                  </Text>
                  <Progress size="sm" isIndeterminate colorScheme="blue" />
                </Box>
              )}

              {uploadStatus === 'success' && (
                <Alert status="success" borderRadius="md">
                  <AlertIcon as={FiCheckCircle} />
                  <Box>
                    <AlertTitle>Upload Successful!</AlertTitle>
                    <AlertDescription>{successMessage}</AlertDescription>
                  </Box>
                </Alert>
              )}

              {uploadStatus === 'error' && errorMessage && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Upload Failed</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Box>
                </Alert>
              )}

              {/* Upload Button */}
              <Button
                leftIcon={<Icon as={FiUpload} />}
                colorScheme="blue"
                size="lg"
                onClick={handleUpload}
                isLoading={isLoading}
                loadingText="Uploading..."
                isDisabled={!selectedFile || !selectedScoreType || isLoading}
                width="100%"
              >
                Upload
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
}